import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getApiUrl } from "../../../../../config/api";
import { useAuth } from "../../../../../firebase/AuthContext";

// ── Attribute renderers ───────────────────────────────────────────────────────

function AttributeBlock({ attr, language }) {
  const { attributeName, dataType, data, notes } = attr;

  return (
    <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 bg-orange-500/5 border-b border-orange-500/20">
        <div className="w-2 h-2 rounded-full bg-orange-500" />
        <h4 className="text-sm font-semibold text-gray-700 capitalize">
          {attributeName}
        </h4>
      </div>

      <div className="p-4">
        {dataType === "list" && Array.isArray(data) && (
          <ul className="space-y-2">
            {data.map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <svg
                  className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm text-gray-700">{String(item)}</span>
              </li>
            ))}
          </ul>
        )}

        {dataType === "table" && Array.isArray(data) && data.length > 0 && (
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  {Object.keys(data[0]).map((col) => (
                    <th
                      key={col}
                      className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200 capitalize whitespace-nowrap"
                    >
                      {col.replace(/_/g, " ")}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => {
                  const cols = Object.keys(data[0]);
                  return (
                    <tr
                      key={i}
                      className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}
                    >
                      {cols.map((col) => {
                        const val = row[col];
                        const display =
                          val === null || val === undefined
                            ? "\u2014"
                            : typeof val === "object"
                              ? JSON.stringify(val)
                              : String(val);
                        return (
                          <td
                            key={col}
                            className="px-3 py-2 text-gray-700 border-b border-gray-100 whitespace-nowrap"
                          >
                            {display}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* table notes */}
        {dataType === "table" && Array.isArray(notes) && notes.length > 0 && (
          <div className="mt-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-xs font-semibold text-yellow-700 mb-1">
              {language === "zh" ? "備註" : "Notes"}
            </p>
            <ul className="space-y-0.5">
              {notes.map((note, i) => (
                <li
                  key={i}
                  className="text-xs text-yellow-800 flex items-start gap-1.5"
                >
                  <span className="shrink-0 mt-0.5 text-yellow-500">•</span>
                  {note}
                </li>
              ))}
            </ul>
          </div>
        )}

        {dataType !== "list" && dataType !== "table" && (
          <p className="text-sm text-gray-700">{String(data)}</p>
        )}
      </div>
    </div>
  );
}

// ── Main Modal ────────────────────────────────────────────────────────────────

function ProductDetailModal({
  show,
  onClose,
  selectedProduct,
  loadingProductData,
  language,
  currentUser,
}) {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [productCategories, setProductCategories] = useState([]);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [selectedLang, setSelectedLang] = useState("en");
  const [selectedImage, setSelectedImage] = useState(null);

  const LANG_LABELS = {
    en: "English",
    cn: "Chinese (Simplified)",
    tw: "Chinese (Traditional)",
    jp: "Japanese",
  };
  const LANG_ORDER = ["en", "cn", "tw", "jp"];

  const fetchProductCategories = useCallback(async () => {
    const token = await currentUser.getIdToken();
    const response = await axios.get(
      `${getApiUrl()}/api/v2/categories/${selectedProduct?.category_id}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    setProductCategories(response.data.data);
  }, [currentUser, selectedProduct?.category_id]);

  const fetchVersions = useCallback(async () => {
    if (!selectedProduct?.product_id || !currentUser) return;
    setLoadingVersions(true);
    try {
      const token = await currentUser.getIdToken();
      const res = await axios.get(
        `${getApiUrl()}/api/v2/products/${selectedProduct.product_id}/versions`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = res.data?.data || [];
      setVersions(data);
      const current =
        data.find(
          (v) => v.version_number === selectedProduct.current_version_label,
        ) ||
        data[data.length - 1] ||
        null;
      setSelectedVersion(current);
      if (current?.version_number) {
        setSelectedLang(current.version_number.split("-")[0] ?? "en");
      }
    } catch (err) {
      console.warn("Failed to fetch versions:", err);
      setVersions([]);
      setSelectedVersion(null);
    } finally {
      setLoadingVersions(false);
    }
  }, [currentUser, selectedProduct]);

  useEffect(() => {
    if (selectedProduct) {
      fetchProductCategories();
      fetchVersions();
    }
  }, [selectedProduct, fetchProductCategories, fetchVersions]);

  const displayData = selectedVersion?.snapshot_data
    ? { ...selectedProduct, ...selectedVersion.snapshot_data }
    : selectedProduct;

  // Saving an edit goes through PUT /api/v2/products/<id>, which requires
  // manage_data — hide the button rather than let the save 403.
  const canEdit = hasRole?.("product.edit");

  /**
   * Open the upload panel in edit mode for the version currently on screen.
   *
   * The IDs travel in the query string so a refresh can re-fetch; the state
   * object is only a prefill so the form paints immediately.
   */
  const handleEditVersion = () => {
    if (!selectedProduct || !selectedVersion) return;
    const langCode = selectedVersion.version_number?.split("-")[0] || "en";
    const search = new URLSearchParams({
      lang: language,
      mode: "edit",
      product_id: String(selectedProduct.product_id),
      version_id: String(selectedVersion.version_id),
    }).toString();

    navigate(
      { pathname: "/product-properties", search: `?${search}` },
      {
        state: {
          mode: "edit",
          productId: selectedProduct.product_id,
          versionId: selectedVersion.version_id,
          versionLabel: selectedVersion.version_number,
          selectedLangCode: langCode,
          productName: displayData.model_id || "",
          productDescription: displayData.description || "",
          productCategory: {
            categoryId: selectedProduct.category_id,
            categoryName: productCategories.category_name || "",
            subCategoryName: productCategories.sub_category || "",
            subSubCategoryName: productCategories.sub_sub_category || "",
          },
          producerId: displayData.producer_id ?? null,
          attributes: displayData.attributes || [],
          images: displayData.images || [],
          pdfUrl: displayData.pdf_url || null,
          rohsCompliant: !!displayData.rohs_compliant,
          reachCompliant: !!displayData.reach_compliant,
          ulComparable: !!displayData.ul_comparable,
        },
      },
    );
  };

  if (!show) return null;

  // Derived version helpers
  const availableLangs = LANG_ORDER.filter((l) =>
    versions.some((v) => v.version_number?.startsWith(l + "-")),
  );
  const versionsForLang = versions.filter((v) =>
    v.version_number?.startsWith(selectedLang + "-"),
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white shadow-2xl w-full max-w-[900px] max-h-[90vh] flex flex-col rounded-2xl overflow-hidden">
        {/* ── Header ──────────────────────────────────────────────── */}
        <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-bold text-gray-900 truncate uppercase tracking-wide">
                {loadingProductData || !selectedProduct
                  ? language === "zh"
                    ? "加載中..."
                    : "Loading..."
                  : selectedProduct.model_id}
              </h2>
              {selectedProduct && (
                <div className="flex items-center gap-2 mt-0.5 text-sm text-gray-500">
                  <span className="px-2 py-0.5 bg-orange-50 border border-orange-200 rounded text-xs text-orange-600 font-medium">
                    {productCategories.category_name}
                  </span>
                  <span className="text-gray-300">|</span>
                  <span className="text-xs text-gray-500">
                    {productCategories.sub_category}
                  </span>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1.5 transition-all ml-4 flex-shrink-0"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* ── Scrollable Body ─────────────────────────────────────── */}
        <div className="overflow-y-auto flex-1 no-scrollbar">
          {loadingProductData ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="relative">
                <div className="w-14 h-14 border-4 border-orange-500/30 rounded-full" />
                <div className="absolute top-0 left-0 w-14 h-14 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
              </div>
              <p className="text-gray-400 mt-4">
                {language === "zh"
                  ? "正在加載產品數據..."
                  : "Loading product data..."}
              </p>
            </div>
          ) : !selectedProduct ? (
            <div className="flex flex-col items-center justify-center py-20">
              <p className="text-gray-500">
                {language === "zh"
                  ? "無法加載產品數據"
                  : "Failed to load product data"}
              </p>
            </div>
          ) : (
            <>
              {/* ── Version Bar (sticky inside body) ──────── */}
              <div className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200 px-6 py-3">
                <div className="flex items-end gap-4 flex-wrap">
                  {/* Language */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      {language === "zh" ? "語言" : "Language"}
                    </label>
                    {loadingVersions ? (
                      <div className="h-9 w-44 bg-gray-200 rounded-lg animate-pulse" />
                    ) : (
                      <select
                        value={selectedLang}
                        onChange={(e) => {
                          const newLang = e.target.value;
                          setSelectedLang(newLang);
                          const first = versions.find((v) =>
                            v.version_number?.startsWith(newLang + "-"),
                          );
                          if (first) setSelectedVersion(first);
                        }}
                        className="w-44 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 font-medium focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400 transition-all"
                      >
                        {availableLangs.map((l) => (
                          <option key={l} value={l}>
                            {LANG_LABELS[l] ?? l.toUpperCase()}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Version */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      {language === "zh" ? "版本" : "Version"}
                    </label>
                    {loadingVersions ? (
                      <div className="h-9 w-36 bg-gray-200 rounded-lg animate-pulse" />
                    ) : (
                      <select
                        value={selectedVersion?.version_id ?? ""}
                        onChange={(e) => {
                          const found = versions.find(
                            (v) => String(v.version_id) === e.target.value,
                          );
                          if (found) setSelectedVersion(found);
                        }}
                        className="w-36 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 font-medium focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400 transition-all"
                      >
                        {versionsForLang.map((v) => {
                          const isCurrent =
                            v.version_number ===
                            selectedProduct.current_version_label;
                          return (
                            <option key={v.version_id} value={v.version_id}>
                              {v.version_number}
                              {isCurrent
                                ? language === "zh"
                                  ? " (目前)"
                                  : " (Current)"
                                : ""}
                            </option>
                          );
                        })}
                      </select>
                    )}
                  </div>

                  {/* Current badge */}
                  {selectedVersion &&
                    selectedVersion.version_number ===
                      selectedProduct.current_version_label && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-orange-50 text-orange-600 border border-orange-200 rounded-lg text-xs font-medium mb-0.5">
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {language === "zh" ? "目前版本" : "Current Version"}
                      </span>
                    )}

                  {/* Edit — opens the upload panel seeded with this version */}
                  {canEdit && (
                    <button
                      type="button"
                      onClick={handleEditVersion}
                      disabled={loadingVersions || !selectedVersion}
                      title={
                        selectedVersion
                          ? language === "zh"
                            ? `編輯 ${selectedVersion.version_number}（將儲存為新版本）`
                            : `Edit ${selectedVersion.version_number} (saves as a new version)`
                          : ""
                      }
                      className="inline-flex items-center gap-1.5 px-3 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-all shadow-sm mb-0.5 ml-auto"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      {language === "zh" ? "編輯此版本" : "Edit this version"}
                    </button>
                  )}

                  {/* <div className="flex items-center gap-2">
                                        <button className="px-4 py-2 bg-blue-600 text-white rounded-lg">
                                            {language === "zh"
                                                ? "複製產品？"
                                                : "Duplicate Product?"}
                                        </button>
                                    </div> */}
                </div>
              </div>

              {/* ── Content ───────────────────────────────── */}
              <div className="p-6 space-y-6">
                {/* Snapshot notice */}
                {selectedVersion &&
                  selectedVersion.version_number !==
                    selectedProduct.current_version_label && (
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
                      <svg
                        className="w-4 h-4 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {language === "zh"
                        ? `正在查看版本 ${selectedVersion.version_number} 的快照，這不是目前的主要版本。`
                        : `Viewing snapshot for version ${selectedVersion.version_number} — this is not the current main version.`}
                    </div>
                  )}

                {/* Compliance Badges */}
                <div className="flex flex-wrap gap-2">
                  {displayData.rohs_compliant && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 text-green-600 rounded-lg text-xs font-medium">
                      <svg
                        className="w-3.5 h-3.5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      RoHS {language === "zh" ? "符合" : "Compliant"}
                    </span>
                  )}
                  {displayData.reach_compliant && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 text-green-600 rounded-lg text-xs font-medium">
                      <svg
                        className="w-3.5 h-3.5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      REACH {language === "zh" ? "符合" : "Compliant"}
                    </span>
                  )}
                  {displayData.ul_comparable && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 text-blue-600 rounded-lg text-xs font-medium">
                      <svg
                        className="w-3.5 h-3.5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      UL {language === "zh" ? "可比" : "Comparable"}
                    </span>
                  )}
                </div>

                {/* PDF Link */}
                {displayData.pdf_url && (
                  <a
                    href={displayData.pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-orange-300 hover:bg-orange-50/50 transition-all group"
                  >
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                      <svg
                        className="w-5 h-5 text-orange-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-gray-900 font-medium text-sm group-hover:text-orange-500 transition-colors">
                        {language === "zh" ? "產品規格書" : "Product Datasheet"}
                        {selectedVersion && (
                          <span className="ml-1.5 text-xs text-gray-400 font-normal">
                            ({selectedVersion.version_number})
                          </span>
                        )}
                      </div>
                      <div className="text-gray-400 text-xs mt-0.5">
                        {language === "zh"
                          ? "點擊下載 PDF"
                          : "Click to download PDF"}
                      </div>
                    </div>
                    <svg
                      className="w-4 h-4 text-gray-400 group-hover:text-orange-500 group-hover:translate-x-0.5 transition-all flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </a>
                )}

                {Array.isArray(displayData.images) &&
                  displayData.images.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 font-medium mb-2">
                        {language === "zh" ? "產品圖片" : "Product Images"}
                        <span className="ml-1 text-gray-400">
                          ({displayData.images.length})
                        </span>
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {displayData.images.map((src, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setSelectedImage(src)}
                            className="block w-20 h-20 rounded-xl overflow-hidden border border-gray-200 hover:border-orange-400 hover:shadow-md transition-all flex-shrink-0"
                          >
                            <img
                              src={src}
                              alt={`${displayData.model_id} img ${i + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                {selectedImage && (
                  <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
                    onClick={() => setSelectedImage(null)}
                  >
                    <img
                      src={selectedImage}
                      alt="Selected Product"
                      className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-lg"
                    />
                  </div>
                )}

                {/* Attributes */}
                {displayData.attributes &&
                  displayData.attributes.length > 0 && (
                    <div className="space-y-4">
                      {displayData.attributes.map((attr, index) => (
                        <AttributeBlock
                          key={index}
                          attr={attr}
                          language={language}
                        />
                      ))}
                    </div>
                  )}

                {/* Features */}
                {displayData.features && displayData.features.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <div className="w-6 h-6 bg-green-100 rounded-md flex items-center justify-center">
                        <svg
                          className="w-3.5 h-3.5 text-green-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      {language === "zh" ? "產品特點" : "Features"}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {displayData.features.map((feature) => (
                        <div
                          key={feature.feature_id}
                          className="flex items-center gap-2 p-2.5 bg-green-50/50 border border-green-100 rounded-lg"
                        >
                          <svg
                            className="w-3.5 h-3.5 text-green-500 flex-shrink-0"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="text-sm text-gray-700 capitalize">
                            {feature.description}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Applications */}
                {displayData.applications &&
                  displayData.applications.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <div className="w-6 h-6 bg-blue-100 rounded-md flex items-center justify-center">
                          <svg
                            className="w-3.5 h-3.5 text-blue-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                            />
                          </svg>
                        </div>
                        {language === "zh" ? "應用領域" : "Applications"}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {displayData.applications.map((app) => (
                          <span
                            key={app.application_id}
                            className="px-3 py-1.5 bg-blue-50 border border-blue-100 text-blue-600 rounded-lg text-xs font-medium capitalize"
                          >
                            {app.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            </>
          )}
        </div>

        {/* ── Footer ──────────────────────────────────────────────── */}
        <div className="flex items-center justify-end px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-sm font-medium transition-all shadow-sm"
          >
            {language === "zh" ? "關閉" : "Close"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProductDetailModal;
